'use client';

import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import { Textarea } from '@packages/shared/src/ui/Textarea/Textarea';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import {
  Accordion,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useIntl, FormattedMessage } from 'react-intl';
import type {
  CustomerInvoiceQuery,
  CustomerSendComplaintRequest,
} from '@packages/gql/generated/shopping/graphql';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, FormProvider } from 'react-hook-form';
import { ControlledSelect } from '@packages/modules/src/ControlledSelect/ControlledSelect';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { SendComplaintDocument } from '@packages/gql/generated/shopping/SendComplaintDocument';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { addressValidationSchema } from '@packages/modules/src/AddressValidation/AddressValidation';
import { toCustomerAddressPayload } from '@packages/modules/src/AddressValidation/addressConverters';
import type { MessageValue } from 'react-intl';
import {
  ProductEditDialog,
  productEditMessages,
  productEditSchema,
} from './ProductEditDialog.client';
import AddressEditDialog from './AddressEditDialog.client';

/* GraphQL */ `
  mutation SendComplaint($payload: CustomerSendComplaintRequest!) {
    sendComplaint(payload: $payload) {
      ... on CustomerGenericSuccessResult {
        success
        errors {
          flags
          elements {
            description
            flags
          }
        }
        warnings {
          flags
        }
      }
      ... on CustomerServerFailure {
        failureType
        failureMessage
      }
    }
  }
`;

const reclamationSchema = z.object({
  pageForm: z.object({
    reason: z.string().optional(),
    description: z.string().min(1),
    sparePartNumber: z.string().optional(),
    dataTypeLabel: z.string().optional(),
  }),
  productEditForm: productEditSchema,
  addressForm: addressValidationSchema(false),
});
type ReclamationFormValues = z.infer<typeof reclamationSchema>;

type Invoice = NonNullable<CustomerInvoiceQuery['customerInvoice']['invoice']>;

export interface ReclamationFormProps {
  invoiceAddress: Invoice['invoiceAddress'];
  deliveryAddress: Invoice['deliveryAddress'];
  customerNumber: string;
  invoiceNumber: string;
  invoiceDate: string;
  productOrderNumber: string;
  productName: string;
  onClose: () => void;
}
const FieldLabel = ({ className, children }: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn(`text-color-grey-dark mt-2`, className)}>{children}</div>
);
const FieldValue = ({ className, children }: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn('font-semibold', className)}>{children}</div>
);

const ReclamationForm = ({
  invoiceAddress,
  deliveryAddress,
  invoiceNumber,
  invoiceDate,
  customerNumber,
  productOrderNumber,
  productName,
  onClose,
}: ReclamationFormProps) => {
  const { formatMessage } = useIntl();
  const [productEdit, setProductEdit] = useState(false);
  const [addressEdit, setAddressEdit] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [mutationResult, sendComplaint] = useAuthMutation(SendComplaintDocument);
  const headerRef = useRef<HTMLDivElement>(null!);
  const methods = useForm<ReclamationFormValues>({
    resolver: zodResolver(reclamationSchema),
    mode: 'all',
    defaultValues: {
      productEditForm: { productOrderNumber, productName },
      addressForm: {
        ...deliveryAddress,
        gender: deliveryAddress.gender || undefined,
        additional: deliveryAddress.additional || undefined,
        primaryPhoneNumber: undefined,
      },
    },
  });
  const {
    register,
    control,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const formSubmitHandler: SubmitHandler<ReclamationFormValues> = async (data) => {
    // reset error state
    setError(undefined);
    // prepare payload
    const payload: CustomerSendComplaintRequest = {
      complaintReason: data.pageForm.reason,
      complaintDescription: data.pageForm.description,
      dataTypeShield: data.pageForm.dataTypeLabel,
      replacementNumber: data.pageForm.sparePartNumber,

      articleDescription: data.productEditForm.productName,
      articleNumber: data.productEditForm.productOrderNumber,

      shippingAddress: {
        firstName: data.addressForm.firstName || deliveryAddress.firstName,
        lastName: data.addressForm.lastName || deliveryAddress.lastName,
        street: data.addressForm.street || deliveryAddress.street,
        houseNumber: data.addressForm.houseNumber || deliveryAddress.houseNumber,
        zipCode: data.addressForm.zipCode || deliveryAddress.zipCode,
        city: data.addressForm.city || deliveryAddress.city,
        countryCode: deliveryAddress?.countryCode || '',
      },

      billingAddress: {
        firstName: invoiceAddress.firstName,
        lastName: invoiceAddress.lastName,
        street: invoiceAddress.street,
        houseNumber: invoiceAddress.houseNumber,
        zipCode: invoiceAddress.zipCode,
        city: invoiceAddress.city,
        countryCode: invoiceAddress?.countryCode || '',
      },

      invoiceDate,
      invoiceNumber,
      customerNo: customerNumber,
    };

    const result = await sendComplaint({ payload });

    const makeGeneralErrorMessage = (message: string) =>
      formatMessage<{ readonly message: MessageValue }>(
        {
          id: 'ReclamationForm.generalErrorMessage',
          defaultMessage:
            'Beim Absenden der Reklamation ist ein Fehler aufgetreten. Bitte versuche es später erneut. {message}',
        },
        { message },
      );

    // errorhandling for network / gql errors
    if (result.error) {
      setError(makeGeneralErrorMessage(result.error.message));
      headerRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    const response = result.data?.sendComplaint;

    // check for CustomerServerFailure
    if (response && response.__typename === 'CustomerServerFailure') {
      setError(makeGeneralErrorMessage(response.failureMessage));
      headerRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    // check for CustomerGenericSuccessResult
    if (response && response.__typename === 'CustomerGenericSuccessResult') {
      // check for server errors
      if (response.errors?.elements && response.errors.elements.length > 0) {
        const errorMessages = response.errors.elements.map((e) => e.description);
        setError(errorMessages.join(', '));
        headerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Dialog onClose={onClose} isOpen>
      <DialogHeader onClose={onClose}>
        <div ref={headerRef}>
          <FormattedMessage
            id="ReclamationForm.dialogTitle"
            defaultMessage="Reklamation vom {date}"
            values={{ date: new Date().toLocaleDateString() }}
          />
        </div>
      </DialogHeader>
      {mutationResult &&
      mutationResult.data?.sendComplaint.__typename === 'CustomerGenericSuccessResult' &&
      mutationResult.data.sendComplaint.success ? (
        <div className="bg-color-grey-light p-4 text-center">
          <div className="">
            <span className="icon-emp-checkmark text-color-success size-[5rem]" />
          </div>
          <div className="text-size-body-xl">
            <FormattedMessage
              id="ReclamationForm.successHeadline"
              defaultMessage="Vielen Dank für deine Nachricht"
            />
          </div>
          <div className="text-size-body-md mt-2">
            <FormattedMessage
              id="ReclamationForm.successMessage"
              defaultMessage="Wir werden uns schnellstmöglich deiner Reklamation widmen und sie bearbeiten."
            />
          </div>
          <div className="mt-2">
            <Button type="button" color="default" onClick={onClose}>
              <FormattedMessage id="ReclamationForm.close" defaultMessage="Schließen" />
            </Button>
          </div>
        </div>
      ) : (
        <FormProvider {...methods}>
          <>
            <AddressEditDialog
              open={addressEdit}
              onClose={() => setAddressEdit(false)}
              address={toCustomerAddressPayload(
                getValues('addressForm'),
                deliveryAddress.countryCode,
              )}
            />
            <ProductEditDialog open={productEdit} onClose={() => setProductEdit(false)} />
            <form onSubmit={(event) => handleSubmit(formSubmitHandler)(event)}>
              <div className="mt-2 mb-4 text-center">
                {error && (
                  <div className="m-2">
                    <Notification severity="error" onClose={() => setError(undefined)}>
                      {error}
                    </Notification>
                  </div>
                )}
                <h3 className="text-size-headline-2xl font-semibold">
                  {formatMessage<{ readonly invoiceNumber: MessageValue }>(
                    {
                      id: 'ReclamationForm.invoiceTitle',
                      defaultMessage: 'Rechnung {invoiceNumber}',
                    },
                    { invoiceNumber },
                  )}
                </h3>
                <p className="text-size-body-sm mt-2">
                  <FormattedMessage
                    id="ReclamationForm.intro"
                    defaultMessage="Um eine schnelle Antwort auch gewährleisten zu können, bitten wir dich, eine Rubrik auszuwählen welche in Verbindung mit deinen Anliegen steht."
                  />
                </p>
              </div>

              <div className="bg-color-grey-light pt-2">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2">
                    <Card className="mx-2 mb-2">
                      <CardContent className="p-2">
                        <FieldLabel className="mt-0">
                          <FormattedMessage
                            id="ReclamationForm.customerNumber"
                            defaultMessage="Kundennummer"
                          />
                        </FieldLabel>
                        <FieldValue>{customerNumber}</FieldValue>

                        <FieldLabel>
                          <FormattedMessage id="ReclamationForm.name" defaultMessage="Name" />
                        </FieldLabel>
                        <FieldValue>
                          {invoiceAddress.firstName} {invoiceAddress.lastName}
                        </FieldValue>

                        <FieldLabel>
                          <FormattedMessage id="ReclamationForm.address" defaultMessage="Adresse" />
                        </FieldLabel>
                        <FieldValue>
                          {invoiceAddress.street} {invoiceAddress.houseNumber}
                        </FieldValue>
                        <FieldValue>
                          {invoiceAddress.zipCode} {invoiceAddress.city}
                        </FieldValue>

                        <FieldLabel>
                          <FormattedMessage
                            id="ReclamationForm.orderNumber"
                            defaultMessage="Bestellnummer"
                          />
                        </FieldLabel>
                        <FieldValue>{invoiceNumber}</FieldValue>
                      </CardContent>
                    </Card>

                    <Card className="mx-2 mb-2">
                      <CardContent className="flex items-start justify-between p-2">
                        <div>
                          <FieldLabel className="mt-0">
                            {formatMessage(productEditMessages.productOrderNumberLabel)}
                          </FieldLabel>
                          <FieldValue>{getValues('productEditForm.productOrderNumber')}</FieldValue>
                          <FieldLabel>
                            {formatMessage(productEditMessages.productNameLabel)}
                          </FieldLabel>
                          <FieldValue>{getValues('productEditForm.productName')}</FieldValue>
                        </div>
                        <Button
                          aria-label={formatMessage({
                            id: 'ReclamationForm.productEditButtonAria',
                            defaultMessage: 'Artikeldaten bearbeiten',
                          })}
                          variant="icon-flat"
                          onClick={() => setProductEdit(true)}
                        >
                          <span className="icon-emp-edit size-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="md:w-1/2">
                    <div className="mx-2 mb-2">
                      <ControlledSelect
                        control={control}
                        controllerName="pageForm.reason"
                        label={formatMessage({
                          id: 'ReclamationForm.reasonLabel',
                          defaultMessage: 'Reklamationsgrund (optional)',
                        })}
                        placeholder={formatMessage({
                          id: 'ReclamationForm.reasonPlaceholder',
                          defaultMessage: 'wähle hier deinen Grund ...',
                        })}
                      >
                        <SelectContent>
                          <SelectItem value="incomplete">
                            <FormattedMessage
                              id="ReclamationForm.reasonMissingPart"
                              defaultMessage="Artikel unvollständig"
                            />
                          </SelectItem>
                          <SelectItem value="damaged">
                            <FormattedMessage
                              id="ReclamationForm.reasonDefect"
                              defaultMessage="Artikel defekt"
                            />
                          </SelectItem>
                          <SelectItem value="wrongProduct">
                            <FormattedMessage
                              id="ReclamationForm.reasonWrongProduct"
                              defaultMessage="Falscher Artikel geliefert"
                            />
                          </SelectItem>
                          <SelectItem value="wrongPrice">
                            <FormattedMessage
                              id="ReclamationForm.reasonWrongPrice"
                              defaultMessage="Falscher Preis"
                            />
                          </SelectItem>
                          <SelectItem value="other">
                            <FormattedMessage
                              id="ReclamationForm.reasonOther"
                              defaultMessage="Sonstiges"
                            />
                          </SelectItem>
                        </SelectContent>
                      </ControlledSelect>
                    </div>

                    <div className="mx-2 mb-2">
                      <Textarea
                        {...register('pageForm.description')}
                        error={!!errors.pageForm?.description?.message}
                        helperText={
                          errors.pageForm?.description
                            ? formatMessage({
                                id: 'ReclamationForm.descriptionError',
                                defaultMessage: 'Dies ist ein Pflichtfeld',
                              })
                            : undefined
                        }
                        className="bg-color-background-base h-[7.5rem]"
                        label={formatMessage({
                          id: 'ReclamationForm.descriptionLabel',
                          defaultMessage: 'Mängelbeschreibung',
                        })}
                        placeholder={formatMessage({
                          id: 'ReclamationForm.descriptionPlaceholder',
                          defaultMessage: 'Welche Mängel hat der Artikel?',
                        })}
                      />
                    </div>
                  </div>
                </div>

                <Accordion type="single" collapsible defaultValue="spare">
                  <AccordionItem value="spare" noStyle className="[&>h3]:p-0">
                    <AccordionSummary className="bg-color-background-base pl-2 text-left">
                      <div>
                        <FormattedMessage
                          id="ReclamationForm.sparePartAccordionTitle"
                          defaultMessage="Benötigst du ein Ersatzteil?"
                        />
                      </div>
                      <div>
                        <FormattedMessage
                          id="ReclamationForm.sparePartAccordionSubtitle"
                          defaultMessage="Ergänze hier bitte folgende Angaben"
                        />
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="flex flex-col p-2 md:flex-row md:gap-2">
                      <Input
                        {...register('pageForm.sparePartNumber')}
                        error={!!errors.pageForm?.sparePartNumber?.message}
                        helperText={errors.pageForm?.sparePartNumber?.message}
                        label={formatMessage({
                          id: 'ReclamationForm.sparePartNumber',
                          defaultMessage: 'Ersatzteilnummer',
                        })}
                        className="bg-color-background-base"
                        containerClassName="w-full md:w-1/2"
                      />
                      <Input
                        {...register('pageForm.dataTypeLabel')}
                        error={!!errors.pageForm?.dataTypeLabel?.message}
                        helperText={errors.pageForm?.dataTypeLabel?.message}
                        label={formatMessage({
                          id: 'ReclamationForm.dataTypeLabel',
                          defaultMessage: 'Daten-Typenschild',
                        })}
                        className="bg-color-background-base w-full"
                        containerClassName="w-full md:w-1/2"
                      />
                    </AccordionDetails>
                  </AccordionItem>
                </Accordion>

                <Accordion type="single" collapsible defaultValue="delivery">
                  <AccordionItem value="delivery" noStyle className="[&>h3]:p-0">
                    <AccordionSummary className="bg-color-background-base pl-2">
                      <FormattedMessage
                        id="ReclamationForm.deliveryAccordionTitle"
                        defaultMessage="Ist deine Lieferadresse abweichend von deiner Kontoanschrift?"
                      />
                    </AccordionSummary>
                    <AccordionDetails className="py-2">
                      <Card>
                        <CardContent className="flex items-start justify-between p-2">
                          <div>
                            <FieldLabel className="mt-0">
                              <FormattedMessage id="ReclamationForm.name" defaultMessage="Name" />
                            </FieldLabel>
                            <FieldValue>
                              {getValues('addressForm.firstName')}{' '}
                              {getValues('addressForm.lastName')}
                            </FieldValue>

                            <FieldLabel>
                              <FormattedMessage
                                id="ReclamationForm.address"
                                defaultMessage="Adresse"
                              />
                            </FieldLabel>
                            <FieldValue>
                              {getValues('addressForm.street')}{' '}
                              {getValues('addressForm.houseNumber')}
                            </FieldValue>
                            <FieldValue>
                              {getValues('addressForm.zipCode')} {getValues('addressForm.city')}
                            </FieldValue>
                          </div>
                          <Button
                            aria-label={formatMessage({
                              id: 'ReclamationForm.addressEditButtonAria',
                              defaultMessage: 'Lieferadresse bearbeiten',
                            })}
                            variant="icon-flat"
                            onClick={() => setAddressEdit(true)}
                          >
                            <span className="icon-emp-edit size-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    </AccordionDetails>
                  </AccordionItem>
                </Accordion>
              </div>

              <div className="m-4 flex justify-center">
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  <FormattedMessage id="ReclamationForm.submit" defaultMessage="Abschicken" />
                </Button>
              </div>
            </form>
          </>
        </FormProvider>
      )}
    </Dialog>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default ReclamationForm;
